import { get } from 'lodash';
import * as React from 'react';

import { Size } from '../../typings/common';
import { PcodeRenderData } from '../../typings/pcode';
import { capturePCodeError } from '../ErrorsReporting/ErrorsReporting';

import { pcodeVer } from './PcodeVersion';

const escapeHtml = require('escape-html');

export interface PcodeRendererFrame {
    iFrame: Size;
    update: (args: object) => void;
    postMessage: (message: string, targetOrigin?: string) => void;
    setSizeOfIFrame: (data: { params: Size }) => void;
}

interface PcodeFrameParams {
    params: Record<string, object | number | string | boolean>;
}

export class PcodeLoader {
    private static loadPromise?: Promise<void>;

    static loadScript(): Promise<void> {
        if (!PcodeLoader.loadPromise) {
            PcodeLoader.loadPromise = new Promise((resolve): void => {
                const script = document.createElement('script');

                script.src = 'https://yastatic.net/pcode/media/frame-api-simple.js';

                script.onload = (): void => resolve();

                document.head.appendChild(script);
            });
        }

        return PcodeLoader.loadPromise;
    }
}

export class PcodeRenderer {
    private frame: PcodeRendererFrame | null;

    /**
     * Рисует превью
     * @param {String} elementId id элемента в котором нужно отрисовать превью
     * @param {Object} data данные для отрисовки превью
     * @static
     */
    public show(elementId: string | React.ReactNode, data: PcodeRenderData): void {
        const frameData = this.prepareFrameParams(elementId, data);

        const FrameAPI = get(window, 'Ya.mediaCode.FrameAPI');

        if (!FrameAPI) {
            capturePCodeError(new Error('Ya.mediaCode.FrameAPI is not defined'));
        }

        try {
            this.frame = new FrameAPI(frameData);
        } catch (e) {
            this.frame = null;
            capturePCodeError(new Error('Ya.mediaCode.FrameAPI is not a constructor'));
        }
    }

    public update(elementId: string | React.ReactNode, data: PcodeRenderData): void {
        const frameData = this.prepareFrameParams(elementId, data);

        if (this.frame) {
            this.frame.update(frameData.params);
        }
    }

    /**
     * @typedef {Object} FrameApiData - параметры для FrameAPI
     * @desc Подробности в задаче PCODE-4054
     *
     * @property {String} element - id DOM элемента, куда будет вставлен iframe
     * @property {String} type - тип баннера ( https://static.yandex.net/partner-code/loaders/banner_formats.json )
     * @property {Number} pcodever - версия партнерского кода
     * @property {Object} [callbacks] - функции вызываемые библиотекой
     * @property {Object} [params] - параметры отображения
     * @property {Object} [params.data] - данные баннера
     * @property {Object} [params.data.direct] - данные баннера директа
     * @property {DataToPcode.ads} [params.data.direct.ads] - массив баннеров
     * @property {Object} [params.data.common]
     */

    /**
     * Преобразует данные в входные параметры библиотеки Frame API
     * @param {String} elementId - id DOM элемента, куда будет вставлен iframe
     * @param {DataToPcode} data - подготовленные данные баннера
     * @return {FrameApiData}
     * @private
     */
    private prepareFrameParams(element: string | React.ReactNode, data: PcodeRenderData): PcodeFrameParams {
        const size = data.size || {
            height: 60,
            width: 240
        };

        const safeAds = data.ads.map(ad => {
            return Object.keys(ad).reduce((acc, adKey) => {
                if (Array.isArray(ad[adKey])) {
                    acc[adKey] = ad[adKey].map((arrayElement: string | undefined) => {
                        if (typeof arrayElement === 'string') {
                            return escapeHtml(arrayElement);
                        }

                        return arrayElement;
                    });
                } else if (typeof ad[adKey] === 'string') {
                    acc[adKey] = escapeHtml(ad[adKey]);
                } else {
                    acc[adKey] = ad[adKey];
                }

                return acc;
            }, {});
        });

        const frameData = {
            element,
            type: data.type,
            pcodever: pcodeVer,
            params: {
                height: size.height,
                width: size.width,

                // тут опциональные параметры стилизации, перечень искать тут:
                // https://github.yandex-team.ru/any/pcode/tree/master/docs_old/call.example, описания нет
                border_radius: false,
                callouts: true,
                header_bg_color: 'FFFFFF',
                font_size: '0.8',
                ad_format: 'direct',
                data: {
                    direct: {
                        ads: safeAds,
                        directTitle: {
                            title: '',
                            url: ''
                        }
                    },
                    common: {
                        linkHead: '',
                        domains: [],
                        isYandex: '1',
                        isYandexPage: '0',
                        isSearchPage: '0',
                        showAdTune: '',
                        stripeType: '1',
                        stripeAnimation: '0',
                        reloadTimeout: '15'
                    }
                }
            }
        };

        return frameData;
    }
}
